import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchBox from '@/components/feature/SearchBox';
import ComicGrid from '@/components/base/ComicGrid';
import { comics, comicCategories, hotComicIds } from '@/mocks/comics';
import { filterComics, sortComics, type FilterState, defaultFilter } from '@/utils/comic';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [category, setCategory] = useState<string>('全部');

  const baseFilter: FilterState = useMemo(
    () => ({ ...defaultFilter, keyword, categories: category === '全部' ? [] : [category] }),
    [keyword, category],
  );

  const results = useMemo(() => sortComics(filterComics(comics, baseFilter), baseFilter.sort), [baseFilter]);

  const hotComics = hotComicIds
    .map((id) => comics.find((comic) => comic.id === id))
    .filter(Boolean)
    .slice(0, 5);

  const reset = () => {
    setCategory('全部');
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <div className="mx-auto mb-6 max-w-2xl">
        <SearchBox
          size="lg"
          initialValue={keyword}
          onSearch={(value) => setSearchParams({ q: value })}
          placeholder="搜索漫画名、作者或标签"
        />
      </div>

      <div className="mb-6 text-center">
        <h1 className="font-heading text-2xl text-foreground-950">
          {keyword ? `“${keyword}” 的搜索结果` : '输入关键词开始搜索'}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          共找到 <span className="font-medium text-primary-600">{results.length}</span> 部相关漫画
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => setCategory('全部')}
          className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition cursor-pointer ${
            category === '全部' ? 'bg-primary-500 font-medium text-background-50' : 'bg-background-100 text-foreground-700'
          }`}
        >
          全部题材
        </button>
        {comicCategories.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setCategory(item.id)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition cursor-pointer ${
              category === item.id ? 'bg-primary-500 font-medium text-background-50' : 'bg-background-100 text-foreground-700'
            }`}
          >
            {item.name}
          </button>
        ))}
        {category !== '全部' && (
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-3.5 py-1.5 text-xs text-foreground-600 transition hover:border-primary-400 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
        )}
      </div>

      {results.length > 0 ? (
        <ComicGrid comics={results} />
      ) : (
        <div>
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-search-eye-line" />
            </span>
            <p className="text-sm text-foreground-600">没有找到「{keyword}」相关漫画</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或看看下面的人气推荐</p>
          </div>
          <div className="mt-8">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-xl text-foreground-950">
              <i className="ri-fire-line text-primary-500" />
              热门漫画推荐
            </h2>
            <ComicGrid comics={hotComics as typeof comics} />
          </div>
        </div>
      )}
    </div>
  );
}